// src/pages/Blog.js
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ArticleCard from '../components/ArticleCard';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import { Meteors } from '../components/ui/meteors';

import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getArticles } from '../utils/api';

export default function Blog() {
  const { darkMode, toggleDarkMode } = useTheme();
  const { 
    user, 
    userLoading, 
    isAuthLoading, 
    handleLogout, 
    openLogin, 
    openRegister 
  } = useAuth();

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['articles'],
    queryFn: () => getArticles().then((res) => res.data),
  });

  const popularArticles = useMemo(() => {
    return [...articles]
      .sort((a, b) => (b.view_count || 0) - (a.view_count || 0))
      .slice(0, 3);
  }, [articles]);

  return (
    <div className="min-h-screen bg-background relative overflow-x-hidden">
      
      {/* Meteors Background - Forced to stay behind content */}
      <div className="absolute inset-0 z-[-1] pointer-events-none overflow-hidden">
        <Meteors count={35} angle={220} />
      </div>

      {/* SEO Meta Tags */}
      <Helmet>
        <title>SErraND Blog | Tips, Guides & Insights for Home Services in Kenya</title>
        <meta 
          name="description" 
          content="Read practical articles, hiring guides, service tips, and industry insights from SErraND. Learn how to find trusted fundis, plumbers, electricians, tutors and more across Kenya." 
        />
        <meta 
          name="keywords" 
          content="SErraND blog, home services Kenya, hire fundi Kenya, plumbing tips Kenya, electrician guide, service provider tips, trusted professionals Kenya, Kitengela services" 
        />
        <meta name="author" content="Marvin Useki" />
        <link rel="canonical" href="https://www.serrand.org/blog/" />

        {/* Open Graph */}
        <meta property="og:title" content="SErraND Blog | Trusted Home Services Insights in Kenya" />
        <meta 
          property="og:description" 
          content="Practical guides and stories to help you hire better and run your service business smarter in Kenya." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.serrand.org/blog/" />
        <meta property="og:image" content="https://www.serrand.org/images/blog-og.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SErraND Blog | Tips for Home Services in Kenya" />
        <meta name="twitter:description" content="Hiring guides, service tips, and insights from Kenya's trusted service marketplace." />
      </Helmet>

      {/* Navbar */}
      <Navbar
        toggleDarkMode={toggleDarkMode}
        darkMode={darkMode}
        user={user}
        userLoading={userLoading}
        isAuthLoading={isAuthLoading}
        handleLogout={handleLogout}
        openLogin={openLogin}
        openRegister={openRegister}
      />

      {/* Main Content - z-10 ensures it stays above meteors */}
      <div className="relative z-10">
        <div className="container max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-10">
            
            {/* Main Content Area */}
            <div className="flex-1 lg:w-2/3">
              <div className="mb-10">
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  SErraND Blog
                </h1>
                <p className="mt-3 text-xl text-muted-foreground">
                  Practical advice, hiring guides, and insights for better home services in Kenya
                </p>
              </div>

              {user?.is_staff && (
                <div className="mb-8">
                  <Button asChild size="lg">
                    <Link to="/admin/home/article/add/">
                      ✍️ Write New Article
                    </Link>
                  </Button>
                </div>
              )}

              {/* Articles List */}
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-muted-foreground mt-4">Loading articles...</p>
                </div>
              ) : articles.length === 0 ? (
                <Card className="p-12 text-center">
                  <CardContent>
                    <p className="text-xl text-muted-foreground mb-6">No articles published yet.</p>
                    {user?.is_staff && (
                      <Button asChild>
                        <Link to="/admin/home/article/add/">Write the First Article</Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-12">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:w-1/3">
              <div className="sticky top-24 space-y-8">
                
                {/* Popular Articles */}
                <Card>
                  <CardHeader>
                    <CardTitle>Popular This Week</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 pt-2">
                    {popularArticles.length > 0 ? (
                      popularArticles.map((article, index) => (
                        <div key={article.id}>
                          <Link 
                            to={`/blog/${article.slug}`}
                            className="group block"
                          >
                            <h3 className="font-medium leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
                              {article.title}
                            </h3>
                          </Link>
                          <p className="text-sm text-muted-foreground mt-1">
                            {article.view_count || 0} views
                          </p>
                          {index < popularArticles.length - 1 && <Separator className="mt-4" />}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">No popular articles yet.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Follow Us */}
                <Card>
                  <CardHeader>
                    <CardTitle>Follow Us</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-6">
                      Get the latest tips, service guides, and platform updates straight to your feed.
                    </p>
                    <AnimatedSocialLinks className="flex flex-col gap-3" />
                  </CardContent>
                </Card>

                {/* About the Blog */}
                <Card>
                  <CardHeader>
                    <CardTitle>About This Blog</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-muted-foreground space-y-4">
                    <p>
                      Real-world advice for homeowners and service providers in Kenya. 
                      From hiring the right fundi to growing your service business.
                    </p>
                    <p>
                      Written with love from Kitengela and beyond.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
